import { useCallback, useEffect, useState } from 'react';
import { AuthProvider, createNote, deleteNote, getNotes, updateNote } from '../http-service';
import type { Note, NoteInput } from '../interfaces';

export type NotesStatus = 'idle' | 'loading' | 'ready' | 'error';

/** Newest first — the order the API returns, kept after local edits too. */
export const newestFirst = (a: Note, b: Note) =>
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();

/**
 * A client's notes, and the writes a coach makes to them.
 *
 * Separate from ClientDataHolder on purpose: notes are their own collection
 * now, loaded by their own request, and a failure here must not take the rest
 * of the dashboard down with it — so this carries its own status rather than
 * joining the holder's all-or-nothing load().
 *
 * State follows the server's answer rather than being set ahead of it. A note
 * that says "saved" and then was not is worse than a second of "Saving…".
 */
export const useNotes = (profileID: string | undefined, auth: AuthProvider) => {
    const [notes, setNotes] = useState<Note[]>([]);
    const [status, setStatus] = useState<NotesStatus>('idle');
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        if (!profileID) return;
        setStatus('loading');
        setError(null);
        try {
            setNotes([...(await getNotes(profileID, auth))].sort(newestFirst));
            setStatus('ready');
        } catch (e) {
            setError(e instanceof Error ? e.message : 'The notes did not load.');
            setStatus('error');
        }
    }, [profileID, auth]);

    useEffect(() => {
        void load();
    }, [load]);

    const create = useCallback(
        async (input: NoteInput) => {
            if (!profileID) throw new Error('No client loaded.');
            const note = await createNote(profileID, input, auth);
            setNotes((prev) => [note, ...prev].sort(newestFirst));
            return note;
        },
        [profileID, auth],
    );

    const update = useCallback(
        async (noteID: string, input: NoteInput) => {
            if (!profileID) throw new Error('No client loaded.');
            const note = await updateNote(profileID, noteID, input, auth);
            setNotes((prev) => prev.map((n) => (n.ID === noteID ? note : n)));
            return note;
        },
        [profileID, auth],
    );

    const remove = useCallback(
        async (noteID: string) => {
            if (!profileID) throw new Error('No client loaded.');
            await deleteNote(profileID, noteID, auth);
            setNotes((prev) => prev.filter((n) => n.ID !== noteID));
        },
        [profileID, auth],
    );

    return { notes, status, error, reload: load, create, update, remove };
};
